/**
 * @author        patrick@zumper.com (Patrick Giddings)
 * @copyright     Copyright (c) 2023, Zumper
 * @description   User bizos
 */

import { isValid } from 'date-fns'
import { Bizo, autoclass } from './base'
import type {
  AgentProfile as AgentProfileType,
  LocalTenantProfile,
  LocalUser,
  NonLocalTenantProfile,
  NonLocalUser,
  RawAgentProfile,
  RawTenantProfile,
  RawUser,
  TenantProfile as TenantProfileType,
  User as UserType,
} from './user.types'

export const AgentProfile: typeof AgentProfileType = autoclass<AgentProfileType, RawAgentProfile>(
  'AgentProfile',
  class AgentProfile extends Bizo<AgentProfileType, RawAgentProfile> {},
  {
    acceptedDocuments: 'accepted_documents',
    address: 'address',
    applicationFee: 'application_fee',
    backgroundUrl: 'background_url',
    brokerageName: 'brokerage_name',
    colors: 'colors',
    createdOn: 'created_on',
    features: 'features',
    headshotUrl: 'headshot_url',
    id: 'user_id',
    leadsProxyEmail: 'leads_proxy_email',
    level: 'level',
    logoUrl: 'logo_url',
    longDescription: 'long_description',
    modifiedOn: 'modified_on',
    preserveZapp: 'preserve_zapp',
    proxyPhoneEnabled: 'proxy_phone_enabled',
    questionIds: 'question_ids',
    region: 'region',
    screeningFee: 'screening_fee',
    shortDescription: 'short_description',
    signature: 'signature',
    syndications: 'syndications',
    url: 'url',
    userId: 'user_id',
    videoUrls: 'video_urls',
    watermarkPlacement: 'watermark_placement',
    watermarkUrl: 'watermark_url',
  }
)

export type AgentProfile = AgentProfileType

export const TenantProfile = autoclass<TenantProfileType, RawTenantProfile>(
  'TenantProfile',
  class TenantProfile extends Bizo<TenantProfileType, RawTenantProfile> {},
  {
    bedrooms: 'bedrooms',
    createdOn: 'created_on',
    employer: 'employer',
    id: 'user_id',
    income: 'income',
    maxPrice: 'max_price',
    minPrice: 'min_price',
    modifiedOn: 'modified_on',
    moveIn: 'move_in', // string YYYY/MM/DD
    pets: 'pets',
    roommates: 'roommates',
    title: 'title',
    userId: 'user_id',
    moveInDate: (t: TenantProfile) => {
      if (t.moveIn) {
        const date = new Date(t.moveIn)
        return isValid(date) ? date : null
      }
      return null
    },
  }
) as unknown as typeof NonLocalTenantProfile & typeof LocalTenantProfile

export type TenantProfile = TenantProfileType

export const User = autoclass<User, RawUser>('User', class User extends Bizo<User, RawUser> {}, {
  agentProfile: { name: 'agent_profile', type: AgentProfile },
  brokerageId: 'brokerage_id',
  dateJoinedTimestamp: 'date_joined_timestamp',
  email: 'email',
  firstName: 'first_name',
  id: 'user_id',
  isActive: 'is_active',
  isAgentRentPaymentOnboarded: 'is_agent_rent_payment_onboarded',
  lastLoginTimestamp: 'last_login_timestamp',
  lastName: 'last_name',
  longName: 'long_name',
  passworded: 'passworded',
  phone: 'phone',
  roles: 'roles',
  tenantFeatures: 'tenant_features',
  tenantProfile: { name: 'tenant_profile', type: TenantProfile },
  toolsActivated: 'tools_activated',
  tos: 'tos',
  unverifiable: 'unverifiable',
  userId: 'user_id',
  userStatus: 'user_status',
  validatedFields: 'validated_fields',
  verifiedOn: 'verified_on',
  fullName: (t: User) => {
    let fullname = t.longName
    if (!fullname) {
      if (t.firstName || t.lastName) {
        fullname = `${t.firstName || ''} ${t.lastName || ''}`.trim()
      }
    }
    return fullname || ''
  },
  avatarName: (t: User) => {
    const firstInitial = t.firstName?.[0]
    const lastInitial = t.lastName?.[0]

    if (firstInitial && lastInitial) {
      return `${firstInitial}${lastInitial}`
    } else if (firstInitial) {
      return firstInitial
    } else if (lastInitial) {
      return lastInitial
    }
    // most likely this won't happen, but this user doesn't have a name
    return ''
  },
}) as unknown as typeof NonLocalUser & typeof LocalUser

export type User = UserType
